// Public server function that returns the full text of an Act for on-site
// rendering. Reads from `legislation_cache` first; if missing/stale, fetches
// once from the official gov.za URL, stores, and returns. Same caching
// contract as the PDF endpoint in src/routes/api/public/legislation.ts.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function stripHtml(html: string): string {
  const cleaned = html
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi, " ")
    .replace(/<header\b[^>]*>[\s\S]*?<\/header>/gi, " ")
    .replace(/<nav\b[^>]*>[\s\S]*?<\/nav>/gi, " ")
    .replace(/<footer\b[^>]*>[\s\S]*?<\/footer>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6]|br|tr)>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  return cleaned
    .split("\n")
    .map((l) => l.replace(/\s+/g, " ").trim())
    .filter((l) => l.length > 0)
    .join("\n");
}

async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(input).buffer as ArrayBuffer,
  );
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const getActFullText = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) =>
    z.object({ slug: z.string().min(3).max(200) }).parse(i),
  )
  .handler(async ({ data }) => {
    const { LEGISLATION } = await import("@/lib/legislation-library");
    const act = LEGISLATION.find(
      (a) => slugify(`${a.title}-${a.number}`) === data.slug,
    );
    if (!act) {
      return {
        ok: false as const,
        error: "not_found",
        act: null,
        fullText: null,
        servedFrom: "none" as const,
      };
    }

    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: cached } = await supabaseAdmin
      .from("legislation_cache")
      .select("full_text, fetched_at, admin_override, fetch_error")
      .eq("slug", data.slug)
      .maybeSingle();

    const fresh =
      cached?.full_text &&
      cached.fetched_at &&
      (cached.admin_override ||
        Date.now() - new Date(cached.fetched_at).getTime() <
          1000 * 60 * 60 * 24 * 30);

    let fullText: string | null = cached?.full_text ?? null;
    let servedFrom: "cache" | "source" | "none" = fresh ? "cache" : "none";
    let error: string | null = null;

    if (!fresh && act.url) {
      try {
        const res = await fetch(act.url, {
          headers: {
            "user-agent":
              "NDLCS-Legislation-Reader/1.0 (+https://nkadimengdannylcs.com)",
            accept: "text/html,application/xhtml+xml,*/*",
          },
          signal: AbortSignal.timeout(20_000),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = (res.headers.get("content-type") ?? "").toLowerCase();
        if (ct.includes("application/pdf")) {
          throw new Error(
            "Source is a PDF; use the 'Full Act (PDF)' download instead.",
          );
        }
        const html = await res.text();
        const text = stripHtml(html);
        if (text.length < 400) throw new Error("Source layout may have changed");
        fullText = text;
        servedFrom = "source";
        const checksum = await sha256Hex(text);
        await supabaseAdmin.from("legislation_cache").upsert(
          {
            slug: data.slug,
            title: act.title,
            act_number: act.number,
            source_url: act.url,
            content_type: "text/plain",
            full_text: text,
            checksum_sha256: checksum,
            fetched_at: new Date().toISOString(),
            fetch_error: null,
          },
          { onConflict: "slug" },
        );
      } catch (e) {
        error = e instanceof Error ? e.message : "Fetch failed";
        if (cached?.full_text) {
          fullText = cached.full_text;
          servedFrom = "cache";
        }
        await supabaseAdmin.from("legislation_cache").upsert(
          {
            slug: data.slug,
            title: act.title,
            act_number: act.number,
            source_url: act.url ?? null,
            fetch_error: error,
            fetched_at: new Date().toISOString(),
          },
          { onConflict: "slug" },
        );
      }
    }

    return {
      ok: true as const,
      error,
      act: {
        title: act.title,
        short: act.short ?? null,
        number: act.number,
        category: act.category,
        summary: act.summary,
        url: act.url ?? null,
        slug: data.slug,
      },
      fullText,
      servedFrom,
    };
  });
